import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';
import DealerPartsDetailView from './DealerPartsDetailView';
import RejectRequestDialog from './RejectRequestDialog';
import { useLazyGetPdfDataQuery } from '../../../../api/endpoints/dealerPartsReturnProcess';
import PdfGenerationService from '../../../../utils/pdfGenerationService';
import { backendColumnMap, DOMESTIC_TYPE, DROPDOWN_FIELDS, NUMERIC_FIELDS, STATIC_DROPDOWN_FIELDS, TEXT_FIELDS } from '../hooks/useDealerPartsReturnProcessData';

const lowerFirst = (str) => (typeof str === 'string' && str.length > 0 ? str.charAt(0).toLowerCase() + str.slice(1) : str);

const getRawOptionsForColumn = (filterOptionsRaw, column, field) => {
    if (!filterOptionsRaw) return [];

    const filterOptions = filterOptionsRaw.options ?? filterOptionsRaw;
    if (!filterOptions) return [];

    if (Array.isArray(filterOptions)) {
        const entry = filterOptions.find((o) => o && [o.column, o.columnName, o.name, o.key, o.field].some((k) => k === column || k === field));
        if (!entry) return [];
        const list = entry.values ?? entry.options ?? entry.items ?? entry.data ?? entry.list ?? [];
        return Array.isArray(list) ? list : [];
    }

    if (typeof filterOptions === 'object') {
        const list = filterOptions[column] ?? filterOptions[field] ?? filterOptions[lowerFirst(column)] ?? [];
        return Array.isArray(list) ? list : [];
    }

    return [];
};

const normalizeOptions = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
        .filter((opt) => opt !== null && opt !== undefined && opt !== '')
        .map((opt) => {
            if (typeof opt === 'object') {
                const value = opt.value ?? opt.id ?? opt.key ?? opt.text ?? opt.name ?? opt.label;
                const rawLabel = opt.label ?? opt.text ?? opt.name ?? opt.displayName ?? value;
                return { value, label: rawLabel };
            }
            return { value: opt, label: opt };
        });
};

const DealerPartsReturnProcessTable = ({
    data,
    loading,
    onPage,
    lazyParams,
    totalCount,
    activeIndex,
    domesticType,
    activeFilters,
    onFiltersChange,
    filterOptions,
    sortField,
    sortOrder,
    onSort,
    handleSendToRM,
    isSendingToRM,
    handleCancelRequest,
    isCancellingRequest,
    handleApproveRequest,
    isApproving,
    handleRejectRequest,
    isRejecting
}) => {
    const { th } = useLanguage();
    const toastRef = useRef(null);
    const [detailDialogVisible, setDetailDialogVisible] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [rejectingRequestId, setRejectingRequestId] = useState(null);
    const [triggerGetPdfData, { isLoading: isLoadingPdf }] = useLazyGetPdfDataQuery();
    const pdfService = useMemo(() => new PdfGenerationService(), []);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const numericMatchModeOptions = useMemo(
        () => [
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS },
            { label: th('filter.greaterThan'), value: FilterMatchMode.GREATER_THAN },
            { label: th('filter.lessThan'), value: FilterMatchMode.LESS_THAN },
            { label: th('filter.greaterThanOrEqual'), value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
            { label: th('filter.lessThanOrEqual'), value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO }
        ],
        [th]
    );

    const isActiveOptions = useMemo(
        () => [
            { value: '1', label: th('common.yes') },
            { value: '0', label: th('common.no') }
        ],
        [th]
    );

    const dropdownOptionsByField = useMemo(() => {
        const result = {};
        DROPDOWN_FIELDS.forEach((field) => {
            result[field] = STATIC_DROPDOWN_FIELDS.includes(field) ? isActiveOptions : normalizeOptions(getRawOptionsForColumn(filterOptions, backendColumnMap[field], field));
        });
        return result;
    }, [filterOptions, isActiveOptions]);

    const renderDropdownFilter = useCallback(
        (field, options) => (
            <Dropdown
                value={options.value}
                options={dropdownOptionsByField[field] || []}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder={th('common.all')}
                showClear
                filter
                filterPlaceholder={th('common.search')}
                className="p-column-filter"
                style={{ minWidth: '12rem' }}
            />
        ),
        [dropdownOptionsByField, th]
    );

    const handleViewDetails = useCallback((rowData) => {
        setSelectedRowData(rowData);
        setDetailDialogVisible(true);
    }, []);

    const hideDetailDialog = useCallback(() => {
        setDetailDialogVisible(false);
        setSelectedRowData(null);
    }, []);

    const handleViewPdf = useCallback(
        async (returnRequestId) => {
            try {
                const result = await triggerGetPdfData(returnRequestId).unwrap();

                if (result?.success && result?.data) {
                    const translations = {
                        title: th('dealerPartsReturnProcess.pdf.title'),
                        serviceCode: th('dealerPartsReturnProcess.pdf.serviceCode'),
                        serviceName: th('dealerPartsReturnProcess.pdf.serviceName'),
                        requestDate: th('dealerPartsReturnProcess.pdf.requestDate'),
                        returnNo: th('dealerPartsReturnProcess.pdf.returnNo'),
                        description: th('dealerPartsReturnProcess.pdf.description'),
                        chassisNo: th('dealerPartsReturnProcess.pdf.chassisNo'),
                        partCode: th('dealerPartsReturnProcess.pdf.partCode'),
                        partName: th('dealerPartsReturnProcess.pdf.partName'),
                        quantity: th('dealerPartsReturnProcess.pdf.quantity'),
                        faultyReason: th('dealerPartsReturnProcess.pdf.faultyReason'),
                        explanation: th('dealerPartsReturnProcess.pdf.explanation'),
                        invoiceNo: th('dealerPartsReturnProcess.pdf.invoiceNo'),
                        invoiceDate: th('dealerPartsReturnProcess.pdf.invoiceDate'),
                        assemblyDate: th('dealerPartsReturnProcess.pdf.assemblyDate'),
                        faultDate: th('dealerPartsReturnProcess.pdf.faultDate'),
                        dayMonthYearDiff: th('dealerPartsReturnProcess.pdf.dayMonthYearDiff'),
                        assemblyKm: th('dealerPartsReturnProcess.pdf.assemblyKm'),
                        faultKm: th('dealerPartsReturnProcess.pdf.faultKm'),
                        kmDiff: th('dealerPartsReturnProcess.pdf.kmDiff'),
                        serviceStamp: th('dealerPartsReturnProcess.pdf.serviceStamp'),
                        date: th('dealerPartsReturnProcess.pdf.date'),
                        authorizedSignature: th('dealerPartsReturnProcess.pdf.authorizedSignature')
                    };

                    const pdfDoc = pdfService.generateReturnRequestPdf(result.data, translations);
                    pdfService.viewPdf(pdfDoc);
                } else {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: th('dealerPartsReturnProcess.pdfError'),
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('PDF oluşturma hatası:', error);
                toastRef.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: th('dealerPartsReturnProcess.pdfError'),
                    life: 3000
                });
            }
        },
        [triggerGetPdfData, pdfService, th]
    );

    const actionBodyTemplate = useCallback(
        (rowData) => {
            const shouldShowPdf = (activeIndex === 1 && rowData.isFaultyPart === true) || activeIndex === 2;

            return (
                <div className="flex gap-2">
                    <Button icon="pi pi-eye" className="p-button-text p-button-sm" onClick={() => handleViewDetails(rowData)} tooltip={th('dealerPartsReturnProcess.actions.viewDetails')} tooltipOptions={{ position: 'top' }} />
                    {shouldShowPdf && (
                        <Button
                            icon="pi pi-file-pdf"
                            className="p-button-text p-button-sm p-button-danger"
                            onClick={() => handleViewPdf(rowData.returnRequestId)}
                            tooltip={th('dealerPartsReturnProcess.actions.viewPdf')}
                            tooltipOptions={{ position: 'top' }}
                            loading={isLoadingPdf}
                        />
                    )}
                </div>
            );
        },
        [activeIndex, handleViewDetails, handleViewPdf, isLoadingPdf, th]
    );

    const isActiveBodyTemplate = useCallback(
        (rowData) => {
            return <span className={`p-tag ${rowData.isActive ? 'p-tag-success' : 'p-tag-danger'}`}>{rowData.isActive ? th('common.yes') : th('common.no')}</span>;
        },
        [th]
    );

    const handleSendToRMClick = useCallback(
        (returnRequestId) => {
            handleSendToRM(returnRequestId, () => {
                setDetailDialogVisible(false);
                setSelectedRowData(null);
            });
        },
        [handleSendToRM]
    );

    const handleCancelRequestClick = useCallback(
        (returnRequestId) => {
            handleCancelRequest(returnRequestId, () => {
                setDetailDialogVisible(false);
                setSelectedRowData(null);
            });
        },
        [handleCancelRequest]
    );

    const handleApproveRequestClick = useCallback(
        (returnRequestId) => {
            confirmDialog({
                message: th('dealerPartsReturnProcess.confirmApprove'),
                header: th('dealerPartsReturnProcess.confirmation'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: th('common.yes'),
                rejectLabel: th('common.cancel'),
                accept: () => {
                    handleApproveRequest(returnRequestId, () => {
                        setDetailDialogVisible(false);
                        setSelectedRowData(null);
                    });
                },
                rejectClassName: 'p-button-outlined'
            });
        },
        [handleApproveRequest, th]
    );

    const handleRejectRequestClick = useCallback((returnRequestId) => {
        setRejectingRequestId(returnRequestId);
        setRejectDialogVisible(true);
    }, []);

    const handleRejectConfirm = useCallback(
        (rejectionReason) => {
            if (rejectingRequestId) {
                handleRejectRequest(rejectingRequestId, rejectionReason, () => {
                    setRejectDialogVisible(false);
                    setRejectingRequestId(null);
                    setDetailDialogVisible(false);
                    setSelectedRowData(null);
                });
            }
        },
        [handleRejectRequest, rejectingRequestId]
    );

    const hideRejectDialog = useCallback(() => {
        setRejectDialogVisible(false);
        setRejectingRequestId(null);
    }, []);

    const tableFilters = useMemo(() => {
        const filters = {};
        TEXT_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS
            };
        });
        NUMERIC_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.EQUALS
            };
        });
        DROPDOWN_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: FilterMatchMode.EQUALS
            };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = {};

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });
            NUMERIC_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.EQUALS;
                }
            });
            DROPDOWN_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                }
            });

            onFiltersChange?.(updated);
        },
        [onFiltersChange]
    );

    const columns = useMemo(() => {
        const makeTextColumn = (field, header, options = {}) => {
            const { body, sortable = true, minWidth = '160px' } = options;
            return {
                field,
                columnKey: field,
                sortable,
                header,
                style: { minWidth },
                body,
                filter: true,
                filterField: field,
                filterPlaceholder: th('common.search'),
                showFilterMenu: true,
                filterMatchModeOptions: textMatchModeOptions,
                showClearButton: false
            };
        };

        const makeNumericColumn = (field, header, options = {}) => {
            const { body, sortable = true, minWidth = '140px' } = options;
            return {
                field,
                columnKey: field,
                sortable,
                header,
                style: { minWidth },
                body,
                filter: true,
                filterField: field,
                filterPlaceholder: th('common.search'),
                showFilterMenu: true,
                filterMatchModeOptions: numericMatchModeOptions,
                showClearButton: false
            };
        };

        const makeDropdownColumn = (field, header, options = {}) => {
            const { body, sortable = true, minWidth = '180px' } = options;
            return {
                field,
                columnKey: field,
                sortable,
                header,
                style: { minWidth },
                body,
                filter: true,
                filterField: field,
                showFilterMenu: false,
                filterElement: (opts) => renderDropdownFilter(field, opts)
            };
        };

        return [
            {
                field: '__actions',
                columnKey: 'action',
                lockedFromPrefs: true,
                lockedPosition: 'start',
                body: actionBodyTemplate,
                bodyClassName: 'text-center',
                headerClassName: 'text-center',
                exportable: false,
                style: { width: '5%', minWidth: '80px' },
                sortable: false
            },
            makeNumericColumn('returnRequestId', th('dealerPartsReturnProcess.returnRequestId'), { minWidth: '140px' }),
            makeDropdownColumn('dealerInfo', th('dealerPartsReturnProcess.dealerName'), { minWidth: '200px' }),
            makeTextColumn('partCode', th('dealerPartsReturnProcess.partCode'), { minWidth: '150px' }),
            makeTextColumn('partDesc', th('dealerPartsReturnProcess.partDesc'), { minWidth: '200px' }),
            makeDropdownColumn('uom', th('dealerPartsReturnProcess.uom'), { minWidth: '120px' }),
            makeNumericColumn('quantity', th('dealerPartsReturnProcess.quantity'), { minWidth: '120px' }),
            makeDropdownColumn('warehouseName', th('dealerWarehouse.warehouseName'), { minWidth: '180px' }),
            makeDropdownColumn('isActive', th('dealerPartsReturnProcess.isActive'), { minWidth: '120px', body: isActiveBodyTemplate }),
            ...(domesticType !== DOMESTIC_TYPE.DOMESTIC ? [makeTextColumn('invoiceNo', th('dealerPartsReturnProcess.invoiceNo'), { minWidth: '150px', body: (rowData) => rowData.invoiceNo || '-' })] : [])
        ];
    }, [actionBodyTemplate, domesticType, isActiveBodyTemplate, numericMatchModeOptions, renderDropdownFilter, textMatchModeOptions, th]);

    return (
        <>
            <Toast ref={toastRef} />
            <ConfirmDialog />
            <RejectRequestDialog visible={rejectDialogVisible} onHide={hideRejectDialog} onReject={handleRejectConfirm} isRejecting={isRejecting} />
            <Dialog header={th('dealerPartsReturnProcess.actions.viewDetails')} visible={detailDialogVisible} style={{ width: '50vw' }} onHide={hideDetailDialog} maximizable>
                <DealerPartsDetailView
                    data={selectedRowData}
                    activeIndex={activeIndex}
                    onSendToRM={handleSendToRMClick}
                    isSendingToRM={isSendingToRM}
                    onCancelRequest={handleCancelRequestClick}
                    isCancellingRequest={isCancellingRequest}
                    onApproveRequest={handleApproveRequestClick}
                    isApproving={isApproving}
                    onRejectRequest={handleRejectRequestClick}
                    isRejecting={isRejecting}
                />
            </Dialog>

            <StickyScrollTable deps={[data, totalCount, loading, activeFilters]} scrollBarPosition="top">
                <PreferencesDataTable
                    tableKey="dealer-parts-return-process"
                    columns={columns}
                    value={Array.isArray(data) ? data : []}
                    totalRecords={totalCount}
                    className="p-datatable-gridlines"
                    showGridlines
                    lazy
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows || 10}
                    onPage={onPage}
                    dataKey="returnRequestId"
                    loading={loading}
                    filterDisplay="row"
                    filters={tableFilters}
                    onFilter={handleTableFilter}
                    emptyMessage={th('dealerPartsReturnProcess.empty')}
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    tableStyle={{ minWidth: '60rem' }}
                    onSort={onSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    sortMode="single"
                    pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
                />
            </StickyScrollTable>
        </>
    );
};

export default DealerPartsReturnProcessTable;
